import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import Overlay from "components/Overlay/Overlay";
import api from "services/api";

const FIELD_TYPES = ["text", "date", "number", "boolean", "dropdown", "language"];

// Sentinel for the Variant select's "None" option. A blank/empty value would
// render as nothing in MUI Select, so "None" needs a real value; it maps to
// null (blank) when saved and from null when loaded.
const VARIANT_NONE_SENTINEL = "__none__";

const formatFieldTypeLabel = (type) => {
  const value = String(type || "").trim();
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : "";
};

const shouldAutoUseLanguageType = (placeholder, label) => {
  const combined = `${placeholder || ""} ${label || ""}`.toLowerCase();
  return combined.includes("language");
};

const smallGreyTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "rgba(128, 128, 128, 0.45)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(128, 128, 128, 0.65)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(128, 128, 128, 0.9)",
    },
  },
};

const ServiceDocumentNewEdit = () => {
  usePageTitle("Service Document");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const { overlayData, setOverlayName, showSnackbar } = useStateContext();

  const [activeDocumentId] = useState(overlayData?.documentId || null);
  const documentId = activeDocumentId;
  const isEditMode = !!documentId;

  // Edit lock for existing documents only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "serviceDocument",
    isEditMode ? documentId : null,
  );

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(overlayData?.document?.Name || "");
  const [description, setDescription] = useState(overlayData?.document?.Description || "");
  const [isActive, setIsActive] = useState(
    overlayData?.document?.IsActive === undefined ? true : !!overlayData?.document?.IsActive,
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [fields, setFields] = useState([]);
  const [previewFields, setPreviewFields] = useState([]);
  const [detectingFields, setDetectingFields] = useState(false);

  // Document classification (category family / language / variant).
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState(
    overlayData?.document?.CategoryID || "",
  );
  const [language, setLanguage] = useState(
    overlayData?.document?.Language || "",
  );
  const [variant, setVariant] = useState(
    // Blank/null stored variant = "None". The select uses a sentinel so the
    // "None" option actually renders when selected (an empty string renders
    // as nothing in MUI Select).
    overlayData?.document?.Variant || VARIANT_NONE_SENTINEL,
  );

  // Load categories for the classification selects (upload + edit).
  useEffect(() => {
    let mounted = true;
    api
      .get("/serviceDocuments/categories")
      .then((response) => {
        if (!mounted) return;
        const data = response?.data || {};
        const list = Array.isArray(data) ? data : data?.categories || [];
        setCategories(list);
      })
      .catch(() => {
        if (mounted) setCategories([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const isUploadFirstState = !isEditMode && !selectedFile;

  const closeOverlay = () => setOverlayName("");

  // Emit socket event so Settings refreshes the Service Documents list live
  const emitSettingsUpdate = (changeAction, recordId) => {
    if (!socket) return;
    socket.emit("settings:saved", {
      settingsType: "ServiceDocument",
      recordId,
      changeAction,
      user: {
        id: currentUser?.id,
        name: currentUser?.Name,
        imageThumbnail: currentUser?.ImageThumbnail,
      },
    });
  };

  useEffect(() => {
    if (!isEditMode) return;

    let mounted = true;
    const loadDocument = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/serviceDocuments/${documentId}`);
        if (!mounted) return;

        const document = response.data || {};
        setName(document.Name || "");
        setDescription(document.Description || "");
        setIsActive(document.IsActive === undefined ? true : !!document.IsActive);
        setCategoryId(document.CategoryID || "");
        setLanguage(document.Language || "");
        setVariant(document.Variant || VARIANT_NONE_SENTINEL);
        setFields(Array.isArray(document.Fields) ? document.Fields : []);
      } catch (error) {
        showSnackbar("Failed to load service document", theme, "error");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadDocument();
    return () => {
      mounted = false;
    };
  }, [documentId, isEditMode, showSnackbar, theme]);

  const refreshFields = async () => {
    if (!documentId) return;
    const response = await api.get(`/serviceDocuments/${documentId}`);
    const document = response.data || {};
    setFields(Array.isArray(document.Fields) ? document.Fields : []);
  };

  const detectFieldsFromFile = async (file) => {
    if (!file) {
      setPreviewFields([]);
      return;
    }

    try {
      setDetectingFields(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/serviceDocuments/detect-placeholders", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const detected = Array.isArray(response?.data?.fields) ? response.data.fields : [];
      setPreviewFields(detected);
    } catch (error) {
      setPreviewFields([]);
      showSnackbar(error?.response?.data?.error || "Failed to detect placeholders", theme, "error");
    } finally {
      setDetectingFields(false);
    }
  };

  const updatePreviewFieldLocal = (placeholder, patch) => {
    setPreviewFields((previous) =>
      previous.map((field) => {
        if (field.OriginalPlaceholder !== placeholder) {
          return field;
        }

        const nextField = { ...field, ...patch };
        if (
          Object.prototype.hasOwnProperty.call(patch, "UserFriendlyLabel") &&
          shouldAutoUseLanguageType(nextField.OriginalPlaceholder, nextField.UserFriendlyLabel)
        ) {
          nextField.FieldType = "language";
        }

        return nextField;
      }),
    );
  };

  const handleSaveDocument = async () => {

    if (!name.trim()) {
      showSnackbar("Name is required", theme, "error");
      return;
    }

    if (!isEditMode && !selectedFile) {
      showSnackbar("DOCX file is required", theme, "error");
      return;
    }

    try {
      setSaving(true);

      if (!isEditMode) {
        const formData = new FormData();
        formData.append("Name", name.trim());
        formData.append("Description", description.trim());
        formData.append("file", selectedFile);
        // Classification fields — sent only when provided (additive).
        if (categoryId) formData.append("CategoryID", String(categoryId));
        if (language.trim()) formData.append("Language", language.trim());
        if (variant !== VARIANT_NONE_SENTINEL && variant.trim())
          formData.append("Variant", variant.trim());
        formData.append(
          "fieldConfigs",
          JSON.stringify(
            previewFields.map((field) => ({
              OriginalPlaceholder: field.OriginalPlaceholder,
              UserFriendlyLabel: (field.UserFriendlyLabel || "").trim() || field.OriginalPlaceholder,
              FieldType: field.FieldType,
              IsRequired: !!field.IsRequired,
              DefaultValue: field.DefaultValue ?? null,
            })),
          ),
        );

        showSnackbar("Uploading document...", theme, "info");

        const createResponse = await api.post("/serviceDocuments/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (createResponse?.data?.template?.id && typeof overlayData?.onSaved === "function") {
          overlayData.onSaved();
        }

        showSnackbar("Service document created", theme, "success");
        emitSettingsUpdate(
          "Created",
          createResponse?.data?.template?.id || createResponse?.data?.id,
        );
        closeOverlay();
      } else {
        await api.put(`/serviceDocuments/${documentId}`, {
          Name: name.trim(),
          Description: description.trim(),
          IsActive: isActive,
          // Classification — send only when provided; explicit empty clears.
          ...(categoryId ? { CategoryID: Number(categoryId) } : { CategoryID: null }),
          ...(language.trim() ? { Language: language.trim() } : { Language: null }),
          ...(variant !== VARIANT_NONE_SENTINEL && variant.trim()
            ? { Variant: variant.trim() }
            : { Variant: null }),
        });

        showSnackbar("Service document updated", theme, "success");
        emitSettingsUpdate("Updated", documentId);

        if (typeof overlayData?.onSaved === "function") {
          overlayData.onSaved();
        }

        closeOverlay();
      }
    } catch (error) {
      console.error("[ServiceDocuments][Client] Save failed", {
        message: error?.message,
        responseData: error?.response?.data,
        status: error?.response?.status,
      });
      showSnackbar(error?.response?.data?.error || "Failed to save service document", theme, "error");
    } finally {
      setSaving(false);
    }
  };

  const updateFieldLocal = (fieldId, patch) => {
    setFields((previous) =>
      previous.map((field) => {
        if (field.id !== fieldId) {
          return field;
        }

        const nextField = { ...field, ...patch };
        if (
          Object.prototype.hasOwnProperty.call(patch, "UserFriendlyLabel") &&
          shouldAutoUseLanguageType(nextField.OriginalPlaceholder, nextField.UserFriendlyLabel)
        ) {
          nextField.FieldType = "language";
        }

        return nextField;
      }),
    );
  };

  const handleSaveField = async (field) => {
    try {
      await api.put(`/serviceDocuments/${documentId}/fields/${field.id}`, {
        UserFriendlyLabel: field.UserFriendlyLabel,
        FieldType: field.FieldType,
        IsRequired: !!field.IsRequired,
      });
      showSnackbar(`Updated ${field.UserFriendlyLabel}`, theme, "success");
      await refreshFields();
    } catch (error) {
      showSnackbar(error?.response?.data?.error || "Failed to update field", theme, "error");
    }
  };

  const footerActions = (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%", justifyContent: "flex-end" }}>
      <Button
        variant="contained"
        startIcon={<SaveRoundedIcon />}
        onClick={handleSaveDocument}
        disabled={saving || loading}
        sx={{ borderRadius: "10px" }}
      >
        {saving ? "Saving..." : "Save"}
      </Button>
    </Box>
  );

  return (
    <>
      <Overlay
        id="service-document-new-edit"
        open={true}
        onClose={closeOverlay}
        title={isEditMode ? "Edit Service Document" : "New Service Document"}
        height="fit-content"
        footer={footerActions}
      >
        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
          }}
        >
          {loading ? (
            <Typography>Loading...</Typography>
          ) : isUploadFirstState ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                px: 2,
              }}
            >
              <Button
                variant="outlined"
                component="label"
                size="large"
                sx={{
                  borderRadius: "10px",
                  minWidth: 220,
                  minHeight: 48,
                }}
              >
                Upload DOCX
                <input
                  type="file"
                  accept=".docx"
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    setSelectedFile(file);
                    detectFieldsFromFile(file);
                    if (file && !name.trim()) {
                      const baseName = String(file.name || "").replace(/\.docx$/i, "").trim();
                      setName(baseName || "");
                    }
                  }}
                />
              </Button>
              <Typography variant="body2" color="text.secondary" align="center">
                Select a DOCX template first. Name and Description will appear next.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2.5}>
              {!isEditMode && (
                <Alert severity="info">
                  Upload a DOCX file with placeholders in this format: {'{ClientName}'}.
                </Alert>
              )}

              <Grid container spacing={2}>
                {!isEditMode && (
                  <Grid size={{ xs: 12 }}>
                    <Button variant="outlined" component="label" sx={{ borderRadius: "10px" }}>
                      {selectedFile ? selectedFile.name : "Upload DOCX"}
                      <input
                        type="file"
                        accept=".docx"
                        hidden
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          setSelectedFile(file);
                          detectFieldsFromFile(file);
                          if (file && !name.trim()) {
                            const baseName = String(file.name || "").replace(/\.docx$/i, "").trim();
                            setName(baseName || "");
                          }
                        }}
                      />
                    </Button>
                  </Grid>
                )}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    required
                    sx={smallGreyTextFieldSx}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    sx={smallGreyTextFieldSx}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label="Category"
                    value={categoryId}
                    onChange={(event) => setCategoryId(event.target.value)}
                    sx={smallGreyTextFieldSx}
                  >
                    <MenuItem value="">
                      <em>None (unclassified)</em>
                    </MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={String(category.id)}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Language"
                    value={language}
                    onChange={(event) => setLanguage(event.target.value)}
                    placeholder="e.g. English, German, Arabic"
                    sx={smallGreyTextFieldSx}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label="Variant"
                    value={variant}
                    onChange={(event) => setVariant(event.target.value)}
                    sx={smallGreyTextFieldSx}
                  >
                    <MenuItem value={VARIANT_NONE_SENTINEL}>None</MenuItem>
                    <MenuItem value="Standard">Standard</MenuItem>
                    <MenuItem value="Notarized">Notarized</MenuItem>
                  </TextField>
                </Grid>
                {isEditMode && (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Switch checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
                      <Typography>{isActive ? "Active" : "Archived"}</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {isEditMode && (
                <>
                  <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
                    Fields
                  </Typography>
                  {fields.length === 0 ? (
                    <Typography color="text.secondary">No placeholders detected.</Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {fields.map((field) => (
                        <Box
                          key={field.id}
                          sx={{
                            p: 1.5,
                            borderRadius: "10px",
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor: theme.palette.background.default,
                          }}
                        >
                          <Grid container spacing={1.5} alignItems="center">
                            <Grid size={{ xs: 12, md: 3 }}>
                              <Typography variant="caption" color="text.secondary">
                                Placeholder
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {`{${field.OriginalPlaceholder}}`}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Label"
                                value={field.UserFriendlyLabel || ""}
                                onChange={(event) =>
                                  updateFieldLocal(field.id, { UserFriendlyLabel: event.target.value })
                                }
                                sx={smallGreyTextFieldSx}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                              <TextField
                                fullWidth
                                select
                                size="small"
                                label="Type"
                                value={field.FieldType || "text"}
                                onChange={(event) => updateFieldLocal(field.id, { FieldType: event.target.value })}
                                sx={smallGreyTextFieldSx}
                              >
                                {FIELD_TYPES.map((type) => (
                                  <MenuItem key={type} value={type}>
                                    {formatFieldTypeLabel(type)}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid size={{ xs: 6, md: 2 }}>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                                <Switch
                                  checked={!!field.IsRequired}
                                  onChange={(event) =>
                                    updateFieldLocal(field.id, { IsRequired: event.target.checked })
                                  }
                                />
                                <Typography variant="caption">Required</Typography>
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 1 }}>
                              <Button size="small" onClick={() => handleSaveField(field)}>
                                Save
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </>
              )}

              {!isEditMode && selectedFile && (
                <>
                  <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                    <Typography variant="h4" fontWeight={700}>
                      Fields
                    </Typography>
                  </Box>
                  {detectingFields ? (
                    <Typography color="text.secondary">Detecting placeholders...</Typography>
                  ) : previewFields.length === 0 ? (
                    <Typography color="text.secondary">No placeholders detected in selected DOCX.</Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {previewFields.map((field, idx) => (
                        <Box
                          key={`${field.OriginalPlaceholder}-${idx}`}
                          sx={{
                            p: 1.5,
                            borderRadius: "10px",
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor: theme.palette.background.default,
                          }}
                        >
                          <Grid container spacing={1.5} alignItems="center">
                            <Grid size={{ xs: 12, md: 3 }}>
                              <Typography variant="caption" color="text.secondary">
                                Placeholder
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {`{${field.OriginalPlaceholder}}`}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Label"
                                value={field.UserFriendlyLabel || ""}
                                onChange={(event) =>
                                  updatePreviewFieldLocal(field.OriginalPlaceholder, {
                                    UserFriendlyLabel: event.target.value,
                                  })
                                }
                                sx={smallGreyTextFieldSx}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                              <TextField
                                fullWidth
                                select
                                size="small"
                                label="Type"
                                value={field.FieldType || "text"}
                                onChange={(event) =>
                                  updatePreviewFieldLocal(field.OriginalPlaceholder, {
                                    FieldType: event.target.value,
                                  })
                                }
                                sx={smallGreyTextFieldSx}
                              >
                                {FIELD_TYPES.map((type) => (
                                  <MenuItem key={type} value={type}>
                                    {formatFieldTypeLabel(type)}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid size={{ xs: 6, md: 2 }}>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                                <Switch
                                  checked={!!field.IsRequired}
                                  onChange={(event) =>
                                    updatePreviewFieldLocal(field.OriginalPlaceholder, {
                                      IsRequired: event.target.checked,
                                    })
                                  }
                                />
                                <Typography variant="caption">Required</Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          )}
        </Box>
      </Overlay>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </>
  );
};

export default ServiceDocumentNewEdit;
